import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import {
  PROVIDER_GENERATE_MUTATION,
  PROVIDER_MODIFY_MUTATION
} from '../../graphql/providerMutations'
import { PROVIDER_QUERY } from '../../graphql/providerQueries'
import { lang } from '../../localization'
import { Provider, ProviderInput } from '../../types/provider'
import ProviderAddHeader from './ProviderAddHeader'
import ProviderBasicInfo from './ProviderBasicInfo'
import { ProviderFooter } from './ProviderFooter'
import { providerFormValidationSchema } from './validations/providerFormValidation'

const PROVIDER_INIT_VALUES: Provider = {
  name: '',
  nameTranslatedList: [
    {
      label: 'Iran, Islamic Republic of',
      value: '',
      nativeLabel: 'فارسی'
    }
  ],
  slogan: '',
  code: '',
  displayProviderCode: '',
  providerCode: '',
  id: '',
  hostName: '',
  rateGroupCode: '',
  rateGroupName: '',
  rateGroupNameTranslatedList: [
    {
      label: 'Iran, Islamic Republic of',
      value: '',
      nativeLabel: 'فارسی'
    }
  ]
}

const ProviderForm = () => {
  const { providerCode } = useParams<{ providerCode: string }>()
  const history = useHistory()
  const theme = useTheme()

  const [getInitialValues, { loading }] = useLazyQuery<{ provider: Provider }>(
    PROVIDER_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    }
  )
  const [initialValues, setinitialValues] =
    useState<Provider>(PROVIDER_INIT_VALUES)

  const getInitialValuesFunction = async () => {
    const { data } = await getInitialValues({
      variables: { providerCode: providerCode }
    })
    setinitialValues({
      ...data?.provider!,
      code: data?.provider.displayProviderCode || ''
    })
  }

  useEffect(() => {
    if (providerCode) {
      getInitialValuesFunction()
    }
  }, [])

  const [providerOnSubmitFunction, { loading: loadingForMutation }] =
    useMutation(
      providerCode ? PROVIDER_MODIFY_MUTATION : PROVIDER_GENERATE_MUTATION,
      {
        onCompleted: (response) => {
          if (providerCode) {
            toast.success(response.modifyProvider.message)
          } else {
            toast.success(response.generateProvider.message)
          }
          apolloClient.cache.evict({
            fieldName: 'providers'
          })
          history.push(`/${lang}/provider`)
        },
        onError: (error) => {
          toast.error(error.message)
        }
      }
    )
  const handleSubmit = async (values: Provider) => {
    const input: ProviderInput = {
      code: values.code,
      name: values.name,
      nameTranslatedList: values.nameTranslatedList,
      slogan: values.slogan,
      hostName: values.hostName,
      rateGroupCode: values.rateGroupCode,
      rateGroupName: values.rateGroupName,
      rateGroupNameTranslatedList: values.rateGroupNameTranslatedList
    }
    await providerOnSubmitFunction(
      providerCode
        ? {
            variables: {
              input: input,
              providerCode: providerCode
            }
          }
        : {
            variables: {
              input: input
            }
          }
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={providerFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        return (
          <>
            <Form>
              <ProviderAddHeader />
              <ProviderBasicInfo
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                isSubmitting={isSubmitting}
                providerCode={providerCode}
              />
              <ProviderFooter
                isSubmitting={isSubmitting}
                loadingForMutation={loadingForMutation}
              />
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default ProviderForm
